// Scanning a pod label with the camera, for devices with no wedge scanner.
//
// Uses the browser's own BarcodeDetector rather than shipping a decoder: it is
// present in Chrome on Android, which is what the Zebras and the phones run,
// and it keeps a WebAssembly blob and a CSP exemption out of the bundle. Where
// it is missing — iOS Safari, desktop Firefox — there is no pretending: the
// operator is told and sent to the typed finder instead.
//
// The camera is a borrowed resource. Every path out of here stops the track,
// because a viewfinder left running behind a closed sheet is both a battery
// drain and a light that stays on for no reason anyone can see.
//
// One read per opening. The first code to come off a frame stops the sampling
// as well as the camera: a stopped video element still holds its last frame,
// and a detector left polling it reads the same label again every tick,
// handing the shell the same pod several times over while it is busy opening
// it once.
//
// On a phone the sheet hangs from the top of the screen. The operator is
// holding the phone up to a label, and a viewfinder rising from the bottom put
// the picture under their thumbs; at the top it is where they are looking.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Sheet } from '@/components/ui/Sheet';
import { useT } from '@/i18n/useT';

/** Not in lib.dom. */
interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<DetectedBarcode[]>;
}
interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
  getSupportedFormats?: () => Promise<string[]>;
}

function detectorCtor(): BarcodeDetectorCtor | null {
  const w = window as unknown as { BarcodeDetector?: BarcodeDetectorCtor };
  return w.BarcodeDetector ?? null;
}

/** Whether this device can decode with the camera at all. */
export function cameraScanSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    detectorCtor() != null &&
    !!navigator.mediaDevices?.getUserMedia
  );
}

/**
 * Formats worth looking for, QR first because that is what a pod label is.
 *
 * The label comes from `interpod_mrp_reports.mo_pod_label_*`, a report on
 * mrp.production, and its QR encodes `o.name` — the bare MO number, e.g.
 * "MO/38861". The same label also carries a Code 128 of the identical value,
 * so either reads. The rest cost nothing and cover labels printed elsewhere in
 * the business.
 */
const WANTED = ['qr_code', 'code_128', 'code_39', 'code_93', 'itf', 'ean_13', 'data_matrix'];

/** How often to look at a frame. Every frame is wasted work on a phone. */
const SAMPLE_MS = 180;

export function CameraScanner({
  open,
  onClose,
  onCode,
  onTypeInstead,
}: {
  open: boolean;
  onClose: () => void;
  onCode: (code: string) => void;
  onTypeInstead: () => void;
}) {
  const t = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setReady(false);
  }, []);

  useEffect(() => {
    if (!open) {
      stop();
      return;
    }
    if (!cameraScanSupported()) {
      setError(t.scanNoCamera);
      return;
    }

    let cancelled = false;
    let timer: number | undefined;
    setError(null);

    (async () => {
      let detector: BarcodeDetectorLike;
      try {
        const Ctor = detectorCtor()!;
        // Asking for a format the device cannot do throws, so intersect first.
        const supported = (await Ctor.getSupportedFormats?.()) ?? WANTED;
        const formats = WANTED.filter((f) => supported.includes(f));
        detector = new Ctor(formats.length ? { formats } : undefined);
      } catch {
        if (!cancelled) setError(t.scanNoCamera);
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          // The back camera, and a resolution high enough to resolve the bars
          // without asking a phone to move 4K frames around.
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        if (cancelled) return;
        const name = (e as { name?: string }).name;
        setError(name === 'NotAllowedError' || name === 'SecurityError' ? t.scanNoPermission : t.scanNoCamera);
        return;
      }

      const tick = async () => {
        const video = videoRef.current;
        if (cancelled || !video || video.readyState < 2) return;
        try {
          const found = await detector.detect(video);
          const code = found[0]?.rawValue?.trim();
          if (code && !cancelled) {
            // Stop before handing over: the pod opens straight away and the
            // camera has no reason to still be running behind it. The
            // sampling stops too, or the frozen last frame reads again.
            cancelled = true;
            window.clearInterval(timer);
            stop();
            onCode(code);
            return;
          }
        } catch {
          // A frame that cannot be decoded is the normal case, not an error.
        }
      };

      timer = window.setInterval(() => void tick(), SAMPLE_MS);
    })();

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Sheet open={open} onClose={onClose} side="top" title={t.scanPod}>
      {error ? (
        <div>
          <p style={{ font: '500 12.5px Manrope,sans-serif', color: 'var(--sub)', marginTop: 0 }}>{error}</p>
          <Button variant="primary" size="lg" onClick={onTypeInstead}>
            {t.scanTypeInstead}
          </Button>
        </div>
      ) : (
        <div>
          <div
            style={{
              position: 'relative',
              width: '100%',
              aspectRatio: '4 / 3',
              borderRadius: 16,
              overflow: 'hidden',
              background: 'var(--ink)',
            }}
          >
            <video
              ref={videoRef}
              playsInline
              muted
              aria-label={t.scanPod}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {/* A window to aim through. Square, because a pod label's code is
                a QR — a letterbox invites people to line up a 1D barcode and
                then wonder why nothing happens. Purely a sighting aid: the
                detector reads the whole frame regardless. */}
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '58%',
                aspectRatio: '1 / 1',
                border: '2px solid var(--accent)',
                borderRadius: 12,
                boxShadow: '0 0 0 9999px rgba(0,0,0,.28)',
              }}
            />
          </div>
          <p
            aria-live="polite"
            style={{ font: '500 12px Manrope,sans-serif', color: 'var(--sub)', margin: '12px 0 14px', textAlign: 'center' }}
          >
            {ready ? t.scanAimAtLabel : t.scanStartingCamera}
          </p>
          <Button variant="soft" size="lg" onClick={onTypeInstead}>
            {t.scanTypeInstead}
          </Button>
        </div>
      )}
    </Sheet>
  );
}
